import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import React, { useEffect, useMemo, useState } from 'react';
import CondorSpinner from '../../Components/CondorSpinner';
import { SectionTitleText } from '../../StyledComponents/Typography';
import { CreateTagModal } from './CreateTagModal';
import styled from 'styled-components';
import cutecondor from '../../assets/cute-condor.png';
import { StyledDataGrid } from '../../StyledComponents/Table';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { Delete, Download, Edit } from '@mui/icons-material';
import { UpdateTagModal } from './UpdateTagModal';
import { DeleteTagModal } from './DeleteTagModal';
import { GridValidRowModel } from '@mui/x-data-grid';
import { DeviceCountCell } from '../../Components/manage-tags/DeviceCountCell';
import { modalStateHook } from '../../Components/ModalStateHook';
import { useQuery } from '@tanstack/react-query';
import { getTags } from '../../services/TagsService';
import { getDeviceTags } from '../../services/DevicesService';

const TableContainer = styled.div`
  margin-top: 3%;
  padding: 0 5% 0 5%;
  height: 80vh;
  margin-bottom: 4em;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
  }

  .button-container {
    display: flex;
    justify-content: flex-end;
    column-gap: 8px;
  }
`;

export interface TagKnown {
  tagId: string;
  tagName: string;
}

export interface DeviceTagKnown {
  tagId: string;
  serialNumber: string;
  canopyDeviceId: string;
}

export interface DeviceTagRecord {
  id: string;
  tagName: string;
  deviceTagSerialNumbers: string[];
  deviceTagsComputed: string;
}

const { useModalState } = modalStateHook();

const createColumns = (
  onEdit: (record: DeviceTagRecord) => void,
  onDelete: (record: DeviceTagRecord) => void,
  onDownload: (record: DeviceTagRecord) => void,
): GridColDef<DeviceTagRecord>[] => [
  { field: 'tagName', headerName: 'Tag', width: 200 },
  { field: 'deviceTagsComputed', headerName: 'Device Count', flex: 1 },
  {
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    width: 200,
    getActions: (params) => [
      <GridActionsCellItem
        key={`edit-${params.id}`}
        icon={<Edit />}
        onClick={() => onEdit(params.row)}
        label="Edit Tag"
      />,
      <GridActionsCellItem
        key={`delete-${params.id}`}
        icon={<Delete />}
        onClick={() => onDelete(params.row)}
        label="Delete Tag"
      />,
      <GridActionsCellItem
        key={`download-${params.id}`}
        icon={<Download />}
        onClick={() => onDownload(params.row)}
        label="Download Serial Numbers"
      />,
    ],
  },
];

export function TagsTable(props: {
  standardTags: { tagId: string; tagName: string }[];
}) {
  const { standardTags } = props;
  const [rows, setRows] = useState<DeviceTagRecord[]>([]);
  const [selectedTag, setSelectedTag] = React.useState<TagKnown | null>(null);

  const createModal = useModalState();
  const updateModal = useModalState();
  const deleteModal = useModalState();

  const transformTagsData = (
    tags: TagKnown[],
    deviceTags: DeviceTagKnown[],
  ): DeviceTagRecord[] => {
    return tags
      .filter((tag) => !standardTags.some((st) => st.tagId === tag.tagId))
      .map((tag) => ({
        id: tag.tagId,
        tagName: tag.tagName,
        deviceTagSerialNumbers: deviceTags
          .filter((dt) => dt.tagId === tag.tagId && dt.serialNumber)
          .map((dt) => dt.serialNumber),
        deviceTagsComputed: DeviceCountCell({
          deviceTags: deviceTags.filter((dt) => dt.tagId === tag.tagId),
        }),
      }));
  };

  const {
    data: tagsData,
    isPending: tagsLoading,
    refetch: refetchTags,
  } = useQuery({
    queryKey: ['manage-tags'],
    queryFn: async () => getTags(100),
  });

  const {
    data: deviceTagsData,
    isPending: deviceTagsLoading,
    refetch: refetchDeviceTags,
  } = useQuery({
    queryKey: ['device-tags'],
    queryFn: async () => getDeviceTags({ limit: 2000 }),
    enabled: !!tagsData,
  });

  const refetchAll = async () => {
    await refetchTags();
    await refetchDeviceTags();
  };

  useEffect(() => {
    const tags = (tagsData?.results ?? []).filter(Boolean) as TagKnown[];
    const deviceTags = (deviceTagsData?.results ?? []).filter(
      Boolean,
    ) as DeviceTagKnown[];
    setRows(transformTagsData(tags, deviceTags));
  }, [
    deviceTagsData,
    tagsData,
    standardTags[0]?.tagId,
    standardTags[1]?.tagId,
  ]);

  const handleTagSelection = (
    tag: DeviceTagRecord,
    action: 'edit' | 'delete',
  ) => {
    setSelectedTag({
      tagId: tag.id,
      tagName: tag.tagName,
    });
    if (action === 'edit') {
      updateModal.open();
    } else {
      deleteModal.open();
    }
  };
  const handleDownloadSerialNumbers = (tag: DeviceTagRecord) => {
    if (
      !tag.deviceTagSerialNumbers ||
      tag.deviceTagSerialNumbers.length === 0
    ) {
      console.error('No serial numbers to download');
      return;
    }
    const csvContent = ['Serial Number', ...tag.deviceTagSerialNumbers].join(
      '\n',
    );
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${tag.tagName}-serial-numbers.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const columns = useMemo(
    () =>
      createColumns(
        (tag) => handleTagSelection(tag, 'edit'),
        (tag) => handleTagSelection(tag, 'delete'),
        (tag) => handleDownloadSerialNumbers(tag),
      ),
    [],
  );

  if (tagsLoading || deviceTagsLoading) {
    return <CondorSpinner />;
  }

  return (
    <>
      <TableContainer>
        <div className="header-container">
          <SectionTitleText>Tags</SectionTitleText>
          <div className="button-container">
            <StyledButton
              onPress={createModal.open}
              buttonRules={ButtonRules.primaryA}
            >
              Create a Tag
            </StyledButton>
          </div>
        </div>

        <StyledDataGrid
          rows={rows}
          columns={columns as GridColDef<GridValidRowModel>[]}
          loading={tagsLoading || deviceTagsLoading}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          slots={{
            noRowsOverlay: () => (
              <div>
                <img
                  src={cutecondor}
                  alt="No tags available to target on"
                  style={{ maxHeight: '70%' }}
                />
              </div>
            ),
          }}
          pageSizeOptions={[5, 10, 50, 100]}
        />
      </TableContainer>
      <CreateTagModal
        isVisible={createModal.isVisible}
        onClose={createModal.close}
        refetch={refetchAll}
      />
      {selectedTag && (
        <>
          <UpdateTagModal
            tag={selectedTag}
            isVisible={updateModal.isVisible}
            onClose={updateModal.close}
            refetch={refetchAll}
          />
          <DeleteTagModal
            tag={selectedTag}
            isVisible={deleteModal.isVisible}
            onClose={deleteModal.close}
            refetch={refetchAll}
          />
        </>
      )}
    </>
  );
}
